/* eslint-disable class-methods-use-this */
import {
  CoordsCannotBeParsedError,
  GeocoderFailedError,
  LocationNotFoundError,
  QUERY_FIELDS,
  QUERY_TYPES,
  type Bias,
  type LatLng,
  type Location
} from './Location.types';
import {
  getNavigatorPosition,
  isValueDegreeFormatCoordinate,
  isValueDMCoordinate,
  isValueDMSCoordinate,
  parseDMS,
  toGoogleLatLng
} from './utils';
import type { LocationAPI } from './LocationAPI.types';

export default class GoogleMapsAPI implements LocationAPI {
  private version: 'v1' | 'v2' = 'v2';

  /** Returns coordinations for given input: either coords, address or current position. */
  getCoords = async (
    coords: string | 'current' | LatLng,
    mapElement?: HTMLDivElement
  ): Promise<LatLng> => {
    if (coords === '' || coords === undefined) throw new Error();
    if (coords === 'current') return getNavigatorPosition();

    let location: LatLng;
    if (typeof coords === 'string') {
      let latLng: number[] = [];
      if (
        isValueDMSCoordinate(coords) ||
        isValueDMCoordinate(coords) ||
        isValueDegreeFormatCoordinate(coords)
      ) {
        latLng = parseDMS(coords);
      } else {
        let splitter;
        if (coords.includes(', ')) {
          splitter = ', ';
        } else if (coords.includes(',')) {
          splitter = ',';
        } else if (coords.includes(' ')) {
          splitter = ' ';
        } else {
          splitter = ', ';
        }

        latLng = coords.split(splitter).map(Number);
      }

      const [lat, lng] = latLng;
      if (Number.isNaN(lat) || Number.isNaN(lng) || lat === undefined || lng === undefined)
        return this.getPlace(coords, mapElement);
      location = { latitude: lat, longitude: lng };
    } else {
      location = coords;
    }

    if (Number.isFinite(location.latitude) && Number.isFinite(location.longitude)) return location;

    throw new Error(CoordsCannotBeParsedError);
  };

  getPlace = async (
    location: string,
    mapElement: HTMLDivElement = document.createElement('div')
  ): Promise<LatLng> => {
    const placesService = new google.maps.places.PlacesService(mapElement);
    const { placeSuggestions, token } = await this.getPlacePredictions(location);
    return new Promise((resolve, reject) => {
      placesService.getDetails(
        {
          placeId: placeSuggestions[0]?.placeId,
          fields: QUERY_FIELDS,
          sessionToken: token
        },
        (place, pStatus) => {
          if (pStatus === google.maps.places.PlacesServiceStatus.OK) {
            resolve({
              latitude: place?.geometry?.location?.lat() ?? NaN,
              longitude: place?.geometry?.location?.lng() ?? NaN
            });
          } else {
            reject(new Error(LocationNotFoundError));
          }
        }
      );
    });
  };

  getPlaceById = async (
    placeId: string,
    sessionToken: google.maps.places.AutocompleteSessionToken = new google.maps.places.AutocompleteSessionToken(),
    mapElement: HTMLDivElement = document.createElement('div')
  ): Promise<Location> => {
    const placesService = new google.maps.places.PlacesService(mapElement);
    return new Promise((resolve, reject) => {
      placesService.getDetails(
        { placeId, fields: QUERY_FIELDS, sessionToken },
        (place, pStatus) => {
          if (pStatus === google.maps.places.PlacesServiceStatus.OK) {
            resolve({
              name: place?.name,
              address: place?.formatted_address,
              latitude: place?.geometry?.location?.lat(),
              longitude: place?.geometry?.location?.lng()
            });
          } else {
            reject(new Error(LocationNotFoundError));
          }
        }
      );
    });
  };

  getAddress = async (
    coords: LatLng
  ): Promise<{
    name: string;
    address?: string;
    latitude: number;
    longitude: number;
  }> => {
    const location = {
      lat: coords.latitude,
      lng: coords.longitude
    };
    const geocoder = new google.maps.Geocoder();
    return new Promise((resolve, reject) => {
      geocoder.geocode(
        { location },
        (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
          if (status === 'OK') {
            const [result] = results ?? [];
            if (result) {
              resolve({
                name: result.formatted_address,
                address: result.formatted_address,
                ...(result.geometry && {
                  latitude: result.geometry.location.lat(),
                  longitude: result.geometry.location.lng()
                })
              });
            } else {
              resolve({
                name: `${coords.latitude}, ${coords.longitude}`,
                latitude: coords.latitude,
                longitude: coords.longitude
              });
            }
          } else {
            reject(new Error(`${GeocoderFailedError}: ${status}`));
          }
        }
      );
    });
  };

  getPlacePredictionsUtil = async (
    location: string,
    bias: Bias = {},
    existingToken?: google.maps.places.AutocompleteSessionToken
  ): Promise<{
    placeSuggestions: {
      placeId: string;
      mainText: string;
      secondaryText: string;
    }[];
    token: google.maps.places.AutocompleteSessionToken;
  }> => {
    const autocompleteService = new google.maps.places.AutocompleteService();
    const optionalParams: Partial<google.maps.places.AutocompletionRequest> = {};
    if (bias?.location) {
      if (bias?.location.center === 'current') {
        await getNavigatorPosition()
          .then(coords => {
            optionalParams.location = toGoogleLatLng(coords);
            if (bias.location) optionalParams.radius = bias.location.radius;
          })
          // no-op user didn't allow location
          .catch(() => {});
      } else {
        optionalParams.location = toGoogleLatLng(bias.location.center);
        optionalParams.radius = bias.location.radius;
      }
    }

    if (bias?.bounds) {
      const [sw, ne] = bias.bounds;
      optionalParams.bounds = new google.maps.LatLngBounds(toGoogleLatLng(sw), toGoogleLatLng(ne));
    }
    return new Promise((resolve, reject) => {
      const token = existingToken ?? new google.maps.places.AutocompleteSessionToken();
      autocompleteService.getPlacePredictions(
        {
          input: location,
          types: QUERY_TYPES,
          sessionToken: token,
          ...optionalParams
        },
        (placePredictions, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK) {
            const res = placePredictions?.map(p => ({
              placeId: p.place_id,
              mainText: p.structured_formatting.main_text,
              secondaryText: p.structured_formatting.secondary_text
            }));

            resolve({ placeSuggestions: res?.length ? res : [], token });
          } else {
            reject(new Error(LocationNotFoundError));
          }
        }
      );
    });
  };

  getPlaceSuggestionsUtil = async (
    location: string,
    bias: Bias = {},
    existingToken?: google.maps.places.AutocompleteSessionToken
  ): Promise<{
    placeSuggestions: {
      placeId: string;
      mainText: string;
      secondaryText: string;
    }[];
    token: google.maps.places.AutocompleteSessionToken;
  }> => {
    const { AutocompleteSuggestion } = google.maps.places;
    const optionalParams: Partial<google.maps.places.AutocompleteRequest> = {};
    if (bias?.location) {
      if (bias?.location.center === 'current') {
        await getNavigatorPosition()
          .then(coords => {
            const b = new google.maps.Circle({
              center: toGoogleLatLng(coords)
            });

            if (bias.location) {
              b.setRadius(bias.location.radius);
            }
            optionalParams.locationBias = b;
          })
          // no-op user didn't allow location
          .catch(() => {});
      } else {
        optionalParams.locationBias = new google.maps.Circle({
          center: toGoogleLatLng(bias.location.center),
          radius: bias.location.radius
        });
      }
    }

    if (bias?.bounds) {
      const [sw, ne] = bias.bounds;
      optionalParams.locationRestriction = new google.maps.LatLngBounds(
        toGoogleLatLng(sw),
        toGoogleLatLng(ne)
      );
    }

    const token = existingToken ?? new google.maps.places.AutocompleteSessionToken();

    const rp: google.maps.places.AutocompleteRequest = {
      input: location,
      sessionToken: token,
      ...optionalParams
    };

    return new Promise((resolve, reject) => {
      AutocompleteSuggestion.fetchAutocompleteSuggestions(rp)
        .then(({ suggestions }) => {
          const res = suggestions
            ?.map(s => s.placePrediction)
            .filter(p => p !== null)
            .map(p => ({
              placeId: p.placeId,
              mainText: p.mainText?.text ?? '',
              secondaryText: p.secondaryText?.text ?? ''
            }));
          resolve({ placeSuggestions: res ?? [], token });
        })
        .catch(e => {
          reject(e);
        });
    });
  };

  /** Returns an array of place prediction objects ('place' can be an establishment, geographic location, or prominent point of interest)
   * and  session token (valid for multiple queries, followed by one place selection).
   */
  getPlacePredictions = async (
    location: string,
    bias?: Bias,
    sessionToken?: google.maps.places.AutocompleteSessionToken
  ): Promise<{
    placeSuggestions: {
      placeId: string;
      mainText: string;
      secondaryText: string;
    }[];
    token: google.maps.places.AutocompleteSessionToken;
  }> => {
    try {
      if (this.version === 'v2') {
        return await this.getPlaceSuggestionsUtil(location, bias, sessionToken);
      }
      return await this.getPlacePredictionsUtil(location, bias, sessionToken);
    } catch (error) {
      if (
        this.version === 'v2' &&
        error instanceof Error &&
        'name' in Error &&
        error.name === 'RpcError'
      ) {
        this.version = 'v1';
        return await this.getPlacePredictions(location, bias, sessionToken);
      }

      throw new Error(LocationNotFoundError);
    }
  };
}
